import { gql } from "graphql-request";

export const GET_POSTS_QUERY = gql`
  query GetPosts(
    $where: postFilterInput
    $pageNo: Int
    $pageSize: Int
    $order: [DynamicSortInput!]
  ) {
    getposts(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      items {
        ItemId
        content
        hashtags
        authorSnapshot {
          firstName
          lastName
          profileImageUrl
          userId
        }
        mentionedUsers {
          userId
          firstName
          lastName
          profileImageUrl
          email
          displayName
        }
        commentCount
        reactionCount {
          heart
          like
          sad
          mad
          laugh
          surprise
          cake
          tada
          clap
          fire
          party
        }
        visibility
        visibleToGroupIds
        visibleToUserIds
        groupId
        attachmentUrls
        saveCount
        stickerCount
        stickers {
          userId
          stickerId
          mediaIndex
          x
          y
          rotation
          scale
          displayName
          profileImageUrl
          placedAt
        }
        CreatedDate
        CreatedBy
        lifeEventType
        lifeEventDate
        newsId
        newsTitle
      }
      totalCount
      totalPages
      pageNo
      pageSize
      hasNextPage
      hasPreviousPage
    }
  }
`;

// Ranked IDs already determine continuation; gateway counts add no value.
export const GET_POST_CARDS_QUERY = gql`
  query GetPostCards(
    $where: postFilterInput
    $pageNo: Int
    $pageSize: Int
    $order: [DynamicSortInput!]
  ) {
    getposts(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      items {
        ItemId
        content
        hashtags
        authorSnapshot {
          firstName
          lastName
          profileImageUrl
          userId
        }
        mentionedUsers {
          userId
          firstName
          lastName
          profileImageUrl
          email
          displayName
        }
        commentCount
        reactionCount {
          heart
          like
          sad
          mad
          laugh
          surprise
          cake
          tada
          clap
          fire
          party
        }
        visibility
        visibleToGroupIds
        visibleToUserIds
        groupId
        attachmentUrls
        saveCount
        stickerCount
        stickers {
          userId
          stickerId
          mediaIndex
          x
          y
          rotation
          scale
          displayName
          profileImageUrl
          placedAt
        }
        CreatedDate
        CreatedBy
        lifeEventType
        lifeEventDate
        newsId
        newsTitle
      }
    }
  }
`;


export const CREATE_POST_MUTATION = gql`
  mutation InsertPost(
    $content: String!
    $hashtags: [String]
    $mentionedUsers: [usersnapshotInput]
    $visibility: String
    $firstName: String
    $lastName: String
    $email: String
    $profileImageUrl: String
    $attachmentUrls: [String]
    $visibleToGroupIds: [String]
    $visibleToUserIds: [String]
    $score: Float
    $userId: String
    $groupId: String
    $lifeEventType: String
    $lifeEventDate: String
    $newsId: String
    $newsTitle: String
  ) {
    insertpost(
      input: {
        content: $content
        hashtags: $hashtags
        mentionedUsers: $mentionedUsers
        visibility: $visibility
        attachmentUrls: $attachmentUrls
        groupId: $groupId
        visibleToGroupIds: $visibleToGroupIds
        visibleToUserIds: $visibleToUserIds
        score: $score
        commentCount: 0
        saveCount: 0
        lifeEventType: $lifeEventType
        lifeEventDate: $lifeEventDate
        newsId: $newsId
        newsTitle: $newsTitle
        reactionCount: {
          like: 0
          mad: 0
          surprise: 0
          cake: 0
          tada: 0
          clap: 0
          fire: 0
          party: 0
          sad: 0
          heart: 0
          laugh: 0
        }
        authorSnapshot: {
          firstName: $firstName
          lastName: $lastName
          email: $email
          profileImageUrl: $profileImageUrl
          userId: $userId
        }
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const UPDATE_POST_MUTATION = gql`
  mutation UpdatePost(
    $itemId: String!
    $content: String
    $mentionedUsers: [usersnapshotInput]
    $hashtags: [String]
    $visibility: String
    $attachmentUrls: [String]
    $visibleToGroupIds: [String]
    $visibleToUserIds: [String]
    $lifeEventType: String
    $lifeEventDate: String
    $newsTitle: String
  ) {
    updatepost(
      where: { ItemId: { eq: $itemId } }
      input: {
        content: $content
        hashtags: $hashtags
        mentionedUsers: $mentionedUsers
        visibility: $visibility
        attachmentUrls: $attachmentUrls
        visibleToGroupIds: $visibleToGroupIds
        visibleToUserIds: $visibleToUserIds
        lifeEventType: $lifeEventType
        lifeEventDate: $lifeEventDate
        newsTitle: $newsTitle
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const UPDATE_POST_SCORE_MUTATION = gql`
  mutation UpdatePost($itemId: String!, $score: Float) {
    updatepost(where: { ItemId: { eq: $itemId } }, input: { score: $score }) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const DELETE_POST_MUTATION = gql`
  mutation DeletePost($itemId: String!) {
    deletepost(
      where: { ItemId: { eq: $itemId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

// Bulk delete: every post created by a user (used by the user-deletion purge).
export const DELETE_MANY_POST_BY_CREATOR = gql`
  mutation DeleteManyPostByCreator($createdBy: String!) {
    deleteManypost(
      where: { CreatedBy: { eq: $createdBy } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// Bulk delete: every post shared in a group (used when deleting a group).
export const DELETE_MANY_POST_BY_GROUP = gql`
  mutation DeleteManyPostByGroup($groupId: String!) {
    deleteManypost(
      where: { groupId: { eq: $groupId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// Bulk update: refresh the author snapshot on every post by this user after a
// profile edit (used by user-name-snapshot-sync — see its module doc for why
// the whole nested object is sent rather than just firstName/lastName).
export const UPDATE_MANY_POST_AUTHOR_SNAPSHOT = gql`
  mutation UpdateManyPostAuthorSnapshot(
    $userId: String!
    $firstName: String
    $lastName: String
    $email: String
    $profileImageUrl: String
  ) {
    updateManypost(
      where: { CreatedBy: { eq: $userId } }
      input: {
        authorSnapshot: {
          firstName: $firstName
          lastName: $lastName
          email: $email
          profileImageUrl: $profileImageUrl
          userId: $userId
        }
      }
    ) {
      acknowledged
      totalImpactedData
      message
    }
  }
`;

// Reactions
export const GET_USER_REACTION_QUERY = gql`
  query GetUserReaction($where: reactionFilterInput) {
    getreactions(
      where: $where
      order: [{ field: "CreatedDate", direction: "DESC" }]
      paging: { pageNo: 1, pageSize: 50 }
    ) {
      items {
        ItemId
        userId
        postId
        commentId
        type
      }
    }
  }
`;

/**
 * This user's reactions across a page of posts, in one round trip.
 *
 * `GET_USER_REACTION_QUERY` answers the same question for a single post, and
 * asking it once per card made a feed of N posts cost N requests. A page is at
 * most `FEED_PAGE_SIZE` posts and a user has at most one reaction each, so
 * `pageSize` is the id count and there is nothing to page through.
 */
export const GET_USER_REACTIONS_FOR_POSTS = gql`
  query GetUserReactionsForPosts($where: reactionFilterInput, $pageSize: Int!) {
    getreactions(
      where: $where
      order: [{ field: "CreatedDate", direction: "DESC" }]
      paging: { pageNo: 1, pageSize: $pageSize }
    ) {
      items {
        ItemId
        userId
        postId
        commentId
        type
      }
    }
  }
`;

export const GET_REACTIONS = gql`
  query GetReactions(
    $where: reactionFilterInput
    $order: [DynamicSortInput!]
    $pageNo: Int
    $pageSize: Int
  ) {
    getreactions(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      items {
        ItemId
        userId
        postId
        commentId
        type
        reactorName
        reactorProfileImageUrl
      }
      totalCount
      totalPages
      hasNextPage
      hasPreviousPage
    }
  }
`;

export const INSERT_REACTION_MUTATION = gql`
  mutation InsertReaction(
    $userId: String!
    $postId: String
    $type: String!
    $commentId: String
    $reactorName: String
    $reactorProfileImageUrl: String
  ) {
    insertreaction(
      input: {
        userId: $userId
        postId: $postId
        type: $type
        commentId: $commentId
        reactorName: $reactorName
        reactorProfileImageUrl: $reactorProfileImageUrl
      }
    ) {
      acknowledged
      itemId
    }
  }
`;

export const UPDATE_REACTION_MUTATION = gql`
  mutation UpdateReaction($itemId: String!, $type: String!) {
    updatereaction(
      where: { ItemId: { eq: $itemId } }
      input: { type: $type }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

// A user's reaction is targeted by (post|comment) + user, not by ItemId. The two
// cases need separate documents: `where` is static, so a single mutation cannot
// omit the irrelevant key, and matching `postId: { eq: null }` would not select
// a comment reaction.
export const DELETE_REACTION_BY_POST_MUTATION = gql`
  mutation DeleteReactionByPost($postId: String!, $userId: String!) {
    deletereaction(
      where: { postId: { eq: $postId }, userId: { eq: $userId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const DELETE_REACTION_BY_COMMENT_MUTATION = gql`
  mutation DeleteReactionByComment($commentId: String!, $userId: String!) {
    deletereaction(
      where: { commentId: { eq: $commentId }, userId: { eq: $userId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

// Bulk delete: every reaction made by a user (used by the user-deletion purge).
export const DELETE_MANY_REACTION_BY_USER = gql`
  mutation DeleteManyReactionByUser($userId: String!) {
    deleteManyreaction(
      where: { userId: { eq: $userId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

export const UPDATE_POST_REACTION_COUNT_MUTATION = gql`
  mutation UpdatePostReactionCount(
    $itemId: String!
    $reactionCount: reactionCountInput!
  ) {
    updatepost(
      where: { ItemId: { eq: $itemId } }
      input: { reactionCount: $reactionCount }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

// Comments
export const GET_COMMENTS_QUERY = gql`
  query GetComments(
    $where: commentFilterInput
    $order: [DynamicSortInput!]
    $pageNo: Int
    $pageSize: Int
  ) {
    getcomments(
      where: $where
      order: $order
      paging: { pageNo: $pageNo, pageSize: $pageSize }
    ) {
      totalCount
      totalPages
      hasNextPage
      hasPreviousPage
      items {
        ItemId
        postId
        parentCommentId
        content
        replyCount
        authorSnapshot {
          firstName
          lastName
          email
          profileImageUrl
        }
        reactionCount {
          like
          mad
          surprise
          cake
          tada
          clap
          fire
          party
          sad
          heart
          laugh
        }
        mentionedUsers {
          userId
          firstName
          lastName
          profileImageUrl
          email
          displayName
        }
        CreatedDate
      }
    }
  }
`;

export const CREATE_COMMENT_MUTATION = gql`
  mutation InsertComment(
    $authorId: String!
    $postId: String!
    $parentCommentId: String
    $content: String!
    $replyCount: Int!
    $firstName: String
    $lastName: String
    $email: String
    $profileImageUrl: String
    $mentionedUsers: [usersnapshotInput]
  ) {
    insertcomment(
      input: {
        authorId: $authorId
        postId: $postId
        parentCommentId: $parentCommentId
        content: $content
        replyCount: $replyCount
        authorSnapshot: {
          firstName: $firstName
          lastName: $lastName
          email: $email
          profileImageUrl: $profileImageUrl
        }
        mentionedUsers: $mentionedUsers
      }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const UPDATE_COMMENT_REACTION_COUNT_MUTATION = gql`
  mutation UpdateCommentReactionCount(
    $itemId: String!
    $reactionCount: reactionCountInput!
  ) {
    updatecomment(
      where: { ItemId: { eq: $itemId } }
      input: { reactionCount: $reactionCount }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const UPDATE_COMMENT_REPLY_COUNT_MUTATION = gql`
  mutation UpdateCommentReplyCount($itemId: String!, $replyCount: Int!) {
    updatecomment(
      where: { ItemId: { eq: $itemId } }
      input: { replyCount: $replyCount }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

export const UPDATE_POST_COMMENT_COUNT_MUTATION = gql`
  mutation UpdatePostCommentCount($itemId: String!, $commentCount: Int!) {
    updatepost(
      where: { ItemId: { eq: $itemId } }
      input: { commentCount: $commentCount }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

// NOTE: no call sites — exported but unused. Converted for consistency so it does
// not become a stale v1 pattern someone copies later.
export const DELETE_COMMENT_MUTATION = gql`
  mutation DeleteComment($itemId: String!) {
    deletecomment(
      where: { ItemId: { eq: $itemId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
    }
  }
`;

// Bulk delete: every comment authored by a user (used by the user-deletion purge).
export const DELETE_MANY_COMMENT_BY_AUTHOR = gql`
  mutation DeleteManyCommentByAuthor($authorId: String!) {
    deleteManycomment(
      where: { authorId: { eq: $authorId } }
      input: { isHardDelete: true }
    ) {
      acknowledged
      totalImpactedData
      itemId
      message
    }
  }
`;

// Bulk update: refresh the author snapshot on every comment by this user after
// a profile edit (used by user-name-snapshot-sync).
export const UPDATE_MANY_COMMENT_AUTHOR_SNAPSHOT = gql`
  mutation UpdateManyCommentAuthorSnapshot(
    $authorId: String!
    $firstName: String
    $lastName: String
    $email: String
    $profileImageUrl: String
  ) {
    updateManycomment(
      where: { authorId: { eq: $authorId } }
      input: {
        authorSnapshot: {
          firstName: $firstName
          lastName: $lastName
          email: $email
          profileImageUrl: $profileImageUrl
        }
      }
    ) {
      acknowledged
      totalImpactedData
      message
    }
  }
`;

// Bulk update: refresh the denormalized actor name/avatar on every reaction by
// this user after a profile edit (used by user-name-snapshot-sync). Reactions
// store the actor as flat `reactorName`/`reactorProfileImageUrl` fields, not a
// nested snapshot object, unlike posts/comments/wiki/news/group memberships.
export const UPDATE_MANY_REACTION_ACTOR = gql`
  mutation UpdateManyReactionActor(
    $userId: String!
    $reactorName: String
    $reactorProfileImageUrl: String
  ) {
    updateManyreaction(
      where: { userId: { eq: $userId } }
      input: {
        reactorName: $reactorName
        reactorProfileImageUrl: $reactorProfileImageUrl
      }
    ) {
      acknowledged
      totalImpactedData
      message
    }
  }
`;

/** Deletes one reaction row by id (post reactions share postId with comment reactions). */
export const DELETE_REACTION_BY_ID_MUTATION = gql`
  mutation DeleteReactionById($itemId: String!) {
    deletereaction(where: { ItemId: { eq: $itemId } }, input: { isHardDelete: true }) {
      acknowledged
      totalImpactedData
    }
  }
`;
